import React, { useState, useRef, useEffect, useContext } from "react";
import { FaAngleLeft } from "react-icons/fa6";
import { IoNotifications } from "react-icons/io5";
import { useSelector } from "react-redux";
import { IoSettingsOutline, IoCalendarOutline } from "react-icons/io5";
import CustomModal from "../Modal";
import CustomButton from "../Button";
import axios from "../../util/axios";
import { Context } from "../Context/ErrorContext";
import { useNavigate } from "react-router-dom";
import { Switch, FormControlLabel } from "@mui/material";

interface HeaderProps {
  toggleSideNav: () => void;
  isSideNavOpen: boolean;
}

const Header = ({ toggleSideNav, isSideNavOpen }: HeaderProps) => {
  const companyData = useSelector((state: any) => state?.company?.companyData);
  const { setError } = useContext(Context);
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLoneWorkerModal, setShowLoneWorkerModal] = useState(false);
  const [durationHour, setDurationHour] = useState("3");
  const [checkinTime, setCheckinTime] = useState("09:00");
  const [checkoutTime, setCheckoutTime] = useState("18:00");
  const [timezone, setTimezone] = useState("UTC");
  const [loading, setLoading] = useState(false);
  const [fetchingDuration, setFetchingDuration] = useState(false);
  const [updatingTimings, setUpdatingTimings] = useState(false);
  const [updatingDuration, setUpdatingDuration] = useState(false);
  const [updatingFeatures, setUpdatingFeatures] = useState(false);
  const [features, setFeatures] = useState({
    is_loneworker_enable: false,
    is_personal_voice_assistant_enable: false,
    is_secure_enable: false,
    is_workplace_emergency_enable: false,
    is_safety_motion_enable: false,
    is_ride_enable: false,
    is_medical_enable: false,
    is_ai_anlysis_enable: false,
    is_crime_enable: false,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Common timezones list
  const timezones = [
    "UTC",
    "EST",
    "GMT",
    "CST",
    "IST",
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const fetchCompanySettings = async () => {
    setFetchingDuration(true);
    try {
      // Fetch lone worker duration
      const durationResult = await axios({
        URL: "/omw/fetch-loneworker-duration",
        method: "GET",
      });
      if (durationResult?.data?.data?.data?.loneworkerduration) {
        setDurationHour(durationResult.data.data.data.loneworkerduration);
      }

      // Fetch company checkin/checkout times
      const timeResult = await axios({
        URL: "/omw/fetch-company-timings",
        method: "GET",
      });
      if (timeResult?.data?.data) {
        if (timeResult.data.data.checkin_time) {
          // Convert time format if needed (e.g., "09:00:00" to "09:00")
          const checkin = timeResult.data.data.checkin_time;
          setCheckinTime(checkin.length > 5 ? checkin.substring(0, 5) : checkin);
        }
        if (timeResult.data.data.checkout_time) {
          const checkout = timeResult.data.data.checkout_time;
          setCheckoutTime(checkout.length > 5 ? checkout.substring(0, 5) : checkout);
        }
        if (timeResult.data.data.timezone) {
          setTimezone(timeResult.data.data.timezone);
        }
      }

      // Fetch feature flags
      const featuresResult = await axios({
        URL: "/omw/subadmin-featureEnabled",
        method: "GET",
      });
      if (featuresResult?.data?.data) {
        setFeatures({
          is_loneworker_enable: featuresResult.data.data.is_loneworker_enable || false,
          is_personal_voice_assistant_enable: featuresResult.data.data.is_personal_voice_assistant_enable || false,
          is_secure_enable: featuresResult.data.data.is_secure_enable || false,
          is_workplace_emergency_enable: featuresResult.data.data.is_workplace_emergency_enable || false,
          is_safety_motion_enable: featuresResult.data.data.is_safety_motion_enable || false,
          is_ride_enable: featuresResult.data.data.is_ride_enable || false,
          is_medical_enable: featuresResult.data.data.is_medical_enable || false,
          is_ai_anlysis_enable: featuresResult.data.data.is_ai_anlysis_enable || false,
          is_crime_enable: featuresResult.data.data.is_crime_enable || false,
        });
      }
    } catch (error: any) {
      // If fetch fails, keep default values
      console.error("Failed to fetch company settings:", error);
    } finally {
      setFetchingDuration(false);
    }
  };

  const handleOpenModal = () => {
    setShowLoneWorkerModal(true);
    setShowDropdown(false);
    fetchCompanySettings();
  };

  const handleUpdateTimings = async () => {
    if (!checkinTime || !checkoutTime) {
      setError("Please set both checkin and checkout times");
      return;
    }

    if (!timezone) {
      setError("Please select a timezone");
      return;
    }

    setUpdatingTimings(true);
    try {
      await axios({
        URL: "/omw/update-company-timings",
        method: "POST",
        data: {
          checkin_time: checkinTime,
          checkout_time: checkoutTime,
          timezone: timezone,
        },
      });
      setError("Company timings updated successfully!");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to update company timings");
    } finally {
      setUpdatingTimings(false);
    }
  };

  const handleUpdateLoneWorker = async () => {
    if (!durationHour) {
      setError("Please select a duration");
      return;
    }

    setUpdatingDuration(true);
    try {
      await axios({
        URL: "/omw/update-loneworker-duration",
        method: "POST",
        data: {
          durationHour: durationHour,
        },
      });
      setError("Lone worker duration updated successfully!");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to update lone worker duration");
    } finally {
      setUpdatingDuration(false);
    }
  };

  const handleUpdateFeatures = async () => {
    setUpdatingFeatures(true);
    try {
      await axios({
        URL: "/omw/subadmin-featureEnabled",
        method: "PUT",
        data: features,
      });
      setError("Features updated successfully!");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to update features");
    } finally {
      setUpdatingFeatures(false);
    }
  };

  const handleFeatureToggle = (featureName: keyof typeof features) => {
    setFeatures((prev) => ({
      ...prev,
      [featureName]: !prev[featureName],
    }));
  };

  return (
    <div className="bg-primary p-4 flex flex-col lg:flex-row gap-5 lg:gap-0 items-center justify-between w-full">
      <div
        className={
          "flex items-center justify-start gap-4 lg:justify-center w-full lg:w-auto"
        }
      >
        <div className={"border border-[#5f6e85] p-1 rounded"}>
          <FaAngleLeft
            className={`text-[#5f6e85] cursor-pointer text-xs ${
              isSideNavOpen ? "" : "rotate-180"
            }`}
            onClick={toggleSideNav}
          />
        </div>
        <div className={"flex items-center text-white"}>
          <div className={"text-sidebarSelectText font-bold text-xl lg:text-2xl"}>
            {companyData?.name}
          </div>
          {/* <div className={"text-primaryText font-bold text-xl lg:text-2xl"}>
            &nbsp;- Distracted Driving Program
          </div> */}
        </div>
      </div>
      <div className={"flex items-center justify-between gap-4 w-full lg:w-auto"}>
        <div className={"flex items-center gap-4"}>
          <div
            className={
              "border border-grayBorder py-1 px-3 lg:py-1.5 rounded-full text-primaryText flex items-center font-medium text-base lg:text-lg gap-2"
            }
          >
            <p className={"text-green !font-bold"}>{companyData?.live_saved}</p>{" "}
            Lives Saved
          </div>
          <div
            className={
              "border border-grayBorder py-1 px-3 lg:py-1.5 rounded-full text-primaryText flex items-center font-medium text-base lg:text-lg gap-2"
            }
          >
            <p className={"text-sidebarSelectText !font-bold"}>
              {companyData?.crashes_prevented}
            </p>{" "}
            Incidents Prevented
          </div>
        </div>
        <div className={"flex items-center gap-2 lg:gap-4"}>
          <div className={"border border-grayBorder rounded-full p-2 lg:p-3"}>
            <IoNotifications className={"text-grayIcon"} />
          </div>
          <div 
            className={"border border-grayBorder rounded-full relative"}
            ref={dropdownRef}
          >
            <img 
              src={"/User.png"} 
              alt="" 
              width={"42px"} 
              height={"42px"} 
              className={"h-[34px] w-[34px] lg:h-[42px] lg:w-[42px] cursor-pointer"} 
              onClick={toggleDropdown}
            />
            {showDropdown && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-primary border border-grayBorder rounded-lg shadow-xl z-50 overflow-hidden">
                <div className="py-1">
                  <div className="px-3 py-2 border-b border-gray/30">
                    <p className="text-grayLight text-xs font-semibold uppercase tracking-wide">Company Settings</p>
                  </div>
                  <div
                    className="px-4 py-3 hover:bg-gray cursor-pointer flex items-center gap-3 transition-colors"
                    onClick={handleOpenModal}
                  >
                    <IoSettingsOutline className="text-sidebarSelectText text-xl" />
                    <span className="text-primaryText text-sm font-medium">
                      Update Lone Worker Time
                    </span>
                  </div>
                  <div
                    className="px-4 py-3 hover:bg-gray cursor-pointer flex items-center gap-3 transition-colors border-t border-gray/30"
                    onClick={() => {
                      navigate("/calendar-settings");
                      setShowDropdown(false);
                    }}
                  >
                    <IoCalendarOutline className="text-sidebarSelectText text-xl" />
                    <span className="text-primaryText text-sm font-medium">
                      Calendar Settings
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Update Lone Worker Duration Modal */}
      <CustomModal
        open={showLoneWorkerModal}
        onClose={() => {
          setShowLoneWorkerModal(false);
        }}
        label="Company Settings"
      >
        <div className="flex flex-col gap-6 mt-4">
          {fetchingDuration ? (
            <div className="flex justify-center items-center py-10">
              <p className="text-primaryText">Loading settings...</p>
            </div>
          ) : (
            <>
              {/* Company Checkin/Checkout Times */}
              <div className="flex flex-col gap-4">
                <h3 className="text-primaryText text-lg font-semibold">Company Timings</h3>
                <div className="flex flex-col gap-2">
                  <label htmlFor="timezone" className="text-primaryText text-sm font-medium">
                    Timezone
                  </label>
                  <select
                    id="timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full p-3 bg-gray text-primaryText rounded-lg border border-grayBorder focus:outline-none focus:border-sidebarSelectText"
                    disabled={updatingTimings}
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="checkinTime" className="text-primaryText text-sm font-medium">
                      Check-in Time
                    </label>
                    <input
                      type="time"
                      id="checkinTime"
                      value={checkinTime}
                      onChange={(e) => setCheckinTime(e.target.value)}
                      className="w-full p-3 bg-gray text-primaryText rounded-lg border border-grayBorder focus:outline-none focus:border-sidebarSelectText"
                      disabled={updatingTimings}
                      step="300"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="checkoutTime" className="text-primaryText text-sm font-medium">
                      Check-out Time
                    </label>
                    <input
                      type="time"
                      id="checkoutTime"
                      value={checkoutTime}
                      onChange={(e) => setCheckoutTime(e.target.value)}
                      className="w-full p-3 bg-gray text-primaryText rounded-lg border border-grayBorder focus:outline-none focus:border-sidebarSelectText"
                      disabled={updatingTimings}
                      step="300"
                    />
                  </div>
                </div>
                <CustomButton
                  title={updatingTimings ? "Updating..." : "Update Timings"}
                  onClick={handleUpdateTimings}
                  disabled={updatingTimings || fetchingDuration}
                  className="!py-3"
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray/30"></div>

              {/* Lone Worker Duration */}
              <div className="flex flex-col gap-4">
                <h3 className="text-primaryText text-lg font-semibold">Lone Worker Duration</h3>
                <div className="flex flex-col gap-2">
                  <label htmlFor="durationHour" className="text-primaryText text-sm font-medium">
                    Duration (Hours)
                  </label>
                  <select
                    id="durationHour"
                    value={durationHour}
                    onChange={(e) => setDurationHour(e.target.value)}
                    className="w-full p-3 bg-gray text-primaryText rounded-lg border border-grayBorder focus:outline-none focus:border-sidebarSelectText"
                    disabled={updatingDuration}
                  >
                    {[2, 3, 4, 5, 6, 7, 8, 9].map((hour) => (
                      <option key={hour} value={hour.toString()}>
                        {hour} {hour === 1 ? "Hour" : "Hours"}
                      </option>
                    ))}
                  </select>
                </div>
                <CustomButton
                  title={updatingDuration ? "Updating..." : "Update Duration"}
                  onClick={handleUpdateLoneWorker}
                  disabled={updatingDuration || fetchingDuration}
                  className="!py-3"
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray/30"></div>

              {/* Feature Enable/Disable */}
              <div className="flex flex-col gap-4">
                <h3 className="text-primaryText text-lg font-semibold">Feature Settings</h3>
                <div className="flex flex-col gap-3">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_loneworker_enable}
                        onChange={() => handleFeatureToggle("is_loneworker_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        Lone Worker Enable
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_personal_voice_assistant_enable}
                        onChange={() => handleFeatureToggle("is_personal_voice_assistant_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        Personal Voice Assistant Enable
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_secure_enable}
                        onChange={() => handleFeatureToggle("is_secure_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        Secure Enable
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_workplace_emergency_enable}
                        onChange={() => handleFeatureToggle("is_workplace_emergency_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        Workplace Emergency Enable
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_safety_motion_enable}
                        onChange={() => handleFeatureToggle("is_safety_motion_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        Safety Motion Enable
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_ride_enable}
                        onChange={() => handleFeatureToggle("is_ride_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        Ride Enable
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_medical_enable}
                        onChange={() => handleFeatureToggle("is_medical_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        Medical Enable
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_ai_anlysis_enable}
                        onChange={() => handleFeatureToggle("is_ai_anlysis_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        AI Analysis Enable
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={features.is_crime_enable}
                        onChange={() => handleFeatureToggle("is_crime_enable")}
                        disabled={updatingFeatures || fetchingDuration}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#5f6e85",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#5f6e85",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-primaryText text-sm font-medium">
                        Crime Enable
                      </span>
                    }
                  />
                </div>
                <CustomButton
                  title={updatingFeatures ? "Updating..." : "Update Features"}
                  onClick={handleUpdateFeatures}
                  disabled={updatingFeatures || fetchingDuration}
                  className="!py-3"
                />
              </div>
            </>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default Header;
